"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableHeatmap = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var includes_1 = tslib_1.__importDefault(require("lodash/includes"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var tail_1 = tslib_1.__importDefault(require("lodash/tail"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_model_1 = require("@gooddata/sdk-model");
var PluggableBaseChart_1 = require("../baseChart/PluggableBaseChart");
var drillDownUtil_1 = require("../drillDownUtil");
var HeatMapConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/HeatMapConfigurationPanel"));
var bucket_1 = require("../../../constants/bucket");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var uiConfig_1 = require("../../../constants/uiConfig");
var SortConfig_1 = require("../../../interfaces/SortConfig");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var heatmapUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/heatmapUiConfigHelper");
var ImplicitDrillDownHelper_1 = require("../../../utils/ImplicitDrillDownHelper");
/**
 * PluggableHeatmap
 *
 * ## Buckets
 *
 * | Name    | Id       | Accepts             |
 * |---------|----------|---------------------|
 * | Measure | measures | measures only       |
 * | Rows    | view     | attributes or dates |
 * | Columns | stack    | attributes or dates |
 *
 * ### Bucket axioms
 *
 * - |Measure| = 1
 * - |Rows| ≤ 1
 * - |Columns| ≤ 1
 *
 * ## Dimensions
 *
 * The PluggableHeatmap always creates the same two dimensional execution.
 *
 * - ⊤ ⇒ [[...Rows], [...Columns, MeasureGroupIdentifier]]
 *
 * ## Sorts
 *
 * Unless the user specifies otherwise, the sorts used by default are:
 *
 * - |Rows| ≥ 1 ⇒ [attributeAreaSort(Rows[0])]
 */
var PluggableHeatmap = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableHeatmap, _super);
    function PluggableHeatmap(props) {
        var _this = _super.call(this, props) || this;
        _this.type = sdk_ui_1.VisualizationTypes.HEATMAP;
        _this.supportedPropertiesList = supportedProperties_1.HEATMAP_SUPPORTED_PROPERTIES;
        _this.initializeProperties(props.visualizationProperties);
        return _this;
    }
    PluggableHeatmap.prototype.getExtendedReferencePoint = function (referencePoint) {
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { uiConfig: cloneDeep_1.default(uiConfig_1.DEFAULT_HEATMAP_UICONFIG) });
        newReferencePoint = bucketHelper_1.removeAllArithmeticMeasuresFromDerived(newReferencePoint);
        newReferencePoint = bucketHelper_1.removeAllDerivedMeasures(newReferencePoint);
        var buckets = bucketHelper_1.limitNumberOfMeasuresInBuckets(clonedReferencePoint.buckets, 1);
        var allAttributes = bucketHelper_1.getAllAttributeItemsWithPreference(buckets, [
            sdk_ui_1.BucketNames.VIEW,
            sdk_ui_1.BucketNames.TREND,
            sdk_ui_1.BucketNames.LOCATION,
            sdk_ui_1.BucketNames.STACK,
            sdk_ui_1.BucketNames.SEGMENT,
        ]);
        var stackItems = bucketHelper_1.getPreferredBucketItems(buckets, [sdk_ui_1.BucketNames.STACK, sdk_ui_1.BucketNames.SEGMENT], [bucket_1.ATTRIBUTE, bucket_1.DATE]);
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var rowItems = allAttributes.filter(function (attribute) {
            return !includes_1.default(stackItems, attribute);
        });
        var columnItems = allAttributes.length > 1 ? tail_1.default(allAttributes) : stackItems;
        set_1.default(newReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: measures,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.VIEW,
                items: rowItems.slice(0, 1),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.STACK,
                items: columnItems.slice(0, 1),
            },
        ]);
        newReferencePoint = heatmapUiConfigHelper_1.setHeatmapUiConfig(newReferencePoint, this.intl, this.type);
        newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, true);
        newReferencePoint = bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        if (!this.featureFlags.enableChartsSorting) {
            newReferencePoint = sort_1.removeSort(newReferencePoint);
        }
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggableHeatmap.prototype.addFilters = function (source, drillConfig, event, backendSupportsElementUris) {
        var clicked = ImplicitDrillDownHelper_1.drillDownFromAttributeLocalId(drillConfig);
        var cutIntersection = (event.drillContext.intersection || []).filter(function (i) {
            return sdk_ui_1.isDrillIntersectionAttributeItem(i.header) &&
                i.header.attributeHeader.localIdentifier === clicked;
        });
        return drillDownUtil_1.addIntersectionFiltersToInsight(source, cutIntersection, backendSupportsElementUris);
    };
    PluggableHeatmap.prototype.getInsightWithDrillDownApplied = function (source, drillDownContext, backendSupportsElementUris) {
        var withFilters = this.addFilters(source, drillDownContext.drillDefinition, drillDownContext.event, backendSupportsElementUris);
        return drillDownUtil_1.modifyBucketsAttributesForDrillDown(withFilters, drillDownContext.drillDefinition);
    };
    PluggableHeatmap.prototype.getDefaultAndAvailableSort = function (measures, viewBy, stackBy) {
        if (!isEmpty_1.default(viewBy) && !isEmpty_1.default(stackBy) && !isEmpty_1.default(measures)) {
            return {
                defaultSort: [
                    sdk_model_1.newAttributeSort(viewBy[0].localIdentifier, "desc"),
                    sdk_model_1.newAttributeSort(stackBy[0].localIdentifier, "asc"),
                ],
                availableSorts: [
                    SortConfig_1.newAvailableSortsGroup(viewBy[0].localIdentifier),
                    SortConfig_1.newAvailableSortsGroup(stackBy[0].localIdentifier),
                ],
            };
        }
        if (!isEmpty_1.default(measures) && !isEmpty_1.default(viewBy)) {
            return {
                defaultSort: [sdk_model_1.newAttributeSort(viewBy[0].localIdentifier, "desc")],
                availableSorts: [
                    SortConfig_1.newAvailableSortsGroup(viewBy[0].localIdentifier, [measures[0].localIdentifier], true, false),
                ],
            };
        }
        if (!isEmpty_1.default(measures) && !isEmpty_1.default(stackBy)) {
            return {
                defaultSort: [sdk_model_1.newAttributeSort(stackBy[0].localIdentifier, "asc")],
                availableSorts: [SortConfig_1.newAvailableSortsGroup(stackBy[0].localIdentifier)],
            };
        }
        return {
            defaultSort: [],
            availableSorts: [],
        };
    };
    PluggableHeatmap.prototype.isSortDisabled = function (referencePoint, availableSorts) {
        var buckets = referencePoint.buckets;
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var viewBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.VIEW);
        var stackBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.STACK);
        var disabled = (viewBy.length < 1 && stackBy.length < 1) || measures.length < 1 || availableSorts.length === 0;
        var disabledExplanation = sort_1.getCustomSortDisabledExplanation(measures, tslib_1.__spreadArrays(viewBy, stackBy), this.intl);
        return {
            disabled: disabled,
            disabledExplanation: disabledExplanation,
        };
    };
    PluggableHeatmap.prototype.getSortConfig = function (referencePoint) {
        var buckets = referencePoint.buckets, properties = referencePoint.properties, previousAvailableSorts = referencePoint.availableSorts;
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var viewBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.VIEW);
        var stackBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.STACK);
        var _a = this.getDefaultAndAvailableSort(measures, viewBy, stackBy), defaultSort = _a.defaultSort, availableSorts = _a.availableSorts;
        var _b = this.isSortDisabled(referencePoint, availableSorts), disabled = _b.disabled, disabledExplanation = _b.disabledExplanation;
        return Promise.resolve(tslib_1.__assign({ supported: true, disabled: disabled, appliedSort: _super.prototype.reuseCurrentSort.call(this, previousAvailableSorts, properties, availableSorts, defaultSort), defaultSort: defaultSort,
            availableSorts: availableSorts }, (disabledExplanation && { disabledExplanation: disabledExplanation })));
    };
    PluggableHeatmap.prototype.renderConfigurationPanel = function (insight) {
        var configPanelElement = this.getConfigPanelElement();
        if (configPanelElement) {
            this.renderFun(react_1.default.createElement(HeatMapConfigurationPanel_1.default, { locale: this.locale, references: this.references, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, colors: this.colors, pushData: this.handlePushData, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags }), configPanelElement);
        }
        return null;
    };
    return PluggableHeatmap;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggableHeatmap = PluggableHeatmap;
//# sourceMappingURL=PluggableHeatmap.js.map